import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import VehicleIdSelect from "components/Common/VehicleIdSelect"
import QuickOnboardVehicleModal from "components/Common/QuickOnboardVehicleModal"
import StatusButtonGroup from "components/Common/StatusButtonGroup"
import { buildVehicleOptionLabel } from "helpers/stock_helpers"
import { useMemo, useCallback, useState } from "react"
import { toast } from "react-toastify"
import { Form, FormFeedback, Label, Button, Row, Col, Card, CardBody } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { getVehicleImporters } from "api/vehicleImporterApi"
import { useAccountBankAccounts } from "hooks/useAccountBankAccount"
import ImporterOnboardingModal from "pages/VehicleImporterPages/VehicleImporterList/ImporterOnboardingModal"
import {
  useCreateAccountVehiclePurchase,
  useEditAccountVehiclePurchase,
} from "hooks/useAccountVehiclePurchase"

const IMPORTER_QUERY_KEY = ["vehicle-importers", "account-vehicle-purchase"]

const PURCHASE_SOURCE_OPTIONS = [
  { value: "importer", label: "Importer", color: "primary", icon: "bx-store" },
  { value: "bank_lc", label: "Bank LC (Import)", color: "primary", icon: "bx-bank" },
]

const AccountVehiclePurchaseAddEdit = ({ modal, toggleModal, isEdit, editedData, onSuccess }) => {
  const queryClient = useQueryClient()
  const [quickOnboardModal, setQuickOnboardModal] = useState(false)
  const [importerModal, setImporterModal] = useState(false)

  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateAccountVehiclePurchase()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditAccountVehiclePurchase()

  const { data: importerResponse, isLoading: importerLoading } = useQuery({
    queryKey: IMPORTER_QUERY_KEY,
    queryFn: () => getVehicleImporters({ all: true, per_page: 200 }),
    enabled: modal,
  })

  const { data: bankAccountResponse, isLoading: bankAccountLoading } = useAccountBankAccounts(
    { all: true, per_page: 200 },
    modal
  )

  const importers = importerResponse?.data?.data || importerResponse?.data || []
  const bankAccounts = bankAccountResponse?.data?.data || bankAccountResponse?.data || []

  const importerOptions = useMemo(
    () => (Array.isArray(importers) ? importers.map(item => ({ value: item.id, label: item.name })) : []),
    [importers]
  )

  const bankAccountOptions = useMemo(
    () =>
      Array.isArray(bankAccounts)
        ? bankAccounts.map(item => ({ value: item.id, label: `${item.label} (${item.bank_name})` }))
        : [],
    [bankAccounts]
  )

  const validationSchema = Yup.object({
    vehicle_importer_id: Yup.mixed().required("Please select an importer"),
    vehicle_id: Yup.mixed().required("Please select a vehicle"),
    source_type: Yup.string().oneOf(["importer", "bank_lc"]).required(),
    lc_number: Yup.string().when("source_type", {
      is: "bank_lc",
      then: schema => schema.required("Please enter the LC number"),
      otherwise: schema => schema.nullable(),
    }),
    bank_account_id: Yup.mixed().when("source_type", {
      is: "bank_lc",
      then: schema => schema.required("Please select the LC bank account"),
      otherwise: schema => schema.nullable(),
    }),
    purchase_price: Yup.number().typeError("Purchase price must be numeric").integer("Purchase price must be a whole number").min(1, "Purchase price must be at least 1").required("Please enter purchase price"),
    purchase_date: Yup.string().required("Please select purchase date"),
    notes: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        ...values,
        vehicle_id: values.vehicle_id?.value ?? values.vehicle_id ?? null,
        purchase_price: Number(values.purchase_price),
        lc_number: values.source_type === "bank_lc" ? values.lc_number?.trim() || null : null,
        bank_account_id: values.source_type === "bank_lc" ? values.bank_account_id || null : null,
        notes: values.notes?.trim() || null,
      }

      const action = isEdit
        ? editMutation({ id: editedData.id, ...payload })
        : createMutation(payload)

      try {
        const res = await action
        toast.success(res?.message ?? `Vehicle purchase ${isEdit ? "updated" : "created"} successfully.`)
        resetForm()
        onSuccess?.(res?.data)
        toggleModal()
      } catch (error) {
        const msg = error?.response?.data?.message || "Failed to save vehicle purchase."
        toast.error(msg)
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal, onSuccess]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      vehicle_importer_id: editedData?.vehicle_importer_id || editedData?.vehicleImporter?.id || "",
      vehicle_id: editedData?.vehicle
        ? {
            label: `${editedData.vehicle.stock_id || ""} - ${editedData.vehicle.full_chassis || ""}`,
            value: editedData.vehicle.id,
          }
        : editedData?.vehicle_id || "",
      source_type: editedData?.source_type || "importer",
      lc_number: editedData?.lc_number || "",
      bank_account_id: editedData?.bank_account_id || editedData?.bankAccount?.id || "",
      purchase_price: editedData?.purchase_price ?? "",
      purchase_date: editedData?.purchase_date || "",
      notes: editedData?.notes || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const loadingAny = createLoading || editLoading || importerLoading || bankAccountLoading

  const handleVehicleOnboarded = useCallback(
    vehicle => {
      if (!vehicle) {
        return
      }
      formik.setFieldValue("vehicle_id", {
        label: buildVehicleOptionLabel(vehicle),
        value: vehicle.id,
      })
    },
    [formik]
  )

  const handleImporterOnboarded = useCallback(
    newImporter => {
      queryClient.invalidateQueries({ queryKey: IMPORTER_QUERY_KEY })
      if (newImporter?.id) {
        formik.setFieldValue("vehicle_importer_id", newImporter.id)
      }
    },
    [formik, queryClient]
  )

  return (
    <>
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Vehicle Purchase" : "Add Vehicle Purchase"}
      size="lg"
    >
      <Form onSubmit={e => { e.preventDefault(); formik.handleSubmit() }}>
        <Card className="border">
          <CardBody>
            <h5 className="mb-3">Purchase Information</h5>
            <Row>
              <Col md={6}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Label htmlFor="vehicle_importer_id" className="mb-0">
                    Importer <span className="text-danger">*</span>
                  </Label>
                  <Button
                    color="link"
                    size="sm"
                    type="button"
                    className="p-0 text-decoration-none"
                    onClick={() => setImporterModal(true)}
                  >
                    + Onboard New Importer
                  </Button>
                </div>
                <FormikCreatableSelect name="vehicle_importer_id" formik={formik} options={importerOptions} loading={importerLoading} />
              </Col>
              <Col md={6}>
                <VehicleIdSelect
                  formik={formik}
                  actions={
                    <Button
                      color="link"
                      size="sm"
                      type="button"
                      className="p-0 text-decoration-none"
                      onClick={() => setQuickOnboardModal(true)}
                    >
                      + Onboard New Vehicle
                    </Button>
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="purchase_price">Purchase Price</Label>
                  <InputField type="number" name="purchase_price" id="purchase_price" min="1" value={formik.values.purchase_price} onChange={formik.handleChange} invalid={formik.touched.purchase_price && !!formik.errors.purchase_price} />
                  {formik.touched.purchase_price && formik.errors.purchase_price && <FormFeedback type="invalid">{formik.errors.purchase_price}</FormFeedback>}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="purchase_date">Purchase Date</Label>
                  <InputField type="date" name="purchase_date" id="purchase_date" value={formik.values.purchase_date} onChange={formik.handleChange} invalid={formik.touched.purchase_date && !!formik.errors.purchase_date} />
                  {formik.touched.purchase_date && formik.errors.purchase_date && <FormFeedback type="invalid">{formik.errors.purchase_date}</FormFeedback>}
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <StatusButtonGroup
                  name="source_type"
                  label="Purchase Source"
                  value={formik.values.source_type}
                  onChange={value => formik.setFieldValue("source_type", value)}
                  options={PURCHASE_SOURCE_OPTIONS}
                />
              </Col>
            </Row>
            {formik.values.source_type === "bank_lc" && (
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label htmlFor="lc_number">LC Number</Label>
                    <InputField type="text" name="lc_number" id="lc_number" value={formik.values.lc_number} onChange={formik.handleChange} invalid={formik.touched.lc_number && !!formik.errors.lc_number} />
                    {formik.touched.lc_number && formik.errors.lc_number && <FormFeedback type="invalid">{formik.errors.lc_number}</FormFeedback>}
                  </div>
                </Col>
                <Col md={6}>
                  <FormikCreatableSelect name="bank_account_id" formik={formik} label="LC Bank Account" options={bankAccountOptions} loading={bankAccountLoading} required />
                </Col>
              </Row>
            )}
          </CardBody>
        </Card>

        <Card className="border mt-3">
          <CardBody>
            <h5 className="mb-3">Additional Details</h5>
            <div className="mb-3">
              <Label htmlFor="notes">Notes</Label>
              <InputField type="textarea" name="notes" id="notes" value={formik.values.notes} onChange={formik.handleChange} invalid={formik.touched.notes && !!formik.errors.notes} />
              {formik.touched.notes && formik.errors.notes && <FormFeedback type="invalid">{formik.errors.notes}</FormFeedback>}
            </div>
          </CardBody>
        </Card>

        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={loadingAny}>
            {loadingAny ? "Saving..." : isEdit ? "Save Changes" : "Add Vehicle Purchase"}
          </Button>
        </div>
      </Form>
    </ThemeModal>

    <QuickOnboardVehicleModal
      isOpen={quickOnboardModal}
      toggle={() => setQuickOnboardModal(false)}
      onOnboarded={handleVehicleOnboarded}
    />

    <ImporterOnboardingModal
      modal={importerModal}
      toggleModal={() => setImporterModal(false)}
      onSuccess={handleImporterOnboarded}
    />
    </>
  )
}

export default AccountVehiclePurchaseAddEdit
